import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppointmentsService from "../appointments/appointmentService";
import PatientAuthorizationService from "../Authorization/authorizationService";
import AuthorizationHoldService from "../AuthorizationHold/AuthorizationHoldService";
import SuperbillService from "./SuperbillService";
import apiBaseUrl from "../../utils/config";
import "./SuperbillCreator.css";

const appointmentAPI = new AppointmentsService();

const DEFAULT_DOCTOR_ID = 15;
const DEFAULT_DOCTOR_NAME = "Default / Not Sure";
const ALLOW_SUPERBILL_FLAG_KEY = "ALLOW_SUPERBILL_WHILE_ON_HOLD";

const SuperbillCreator = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    officeId: "",
    officeName: "",
    doctorId: DEFAULT_DOCTOR_ID,
    doctorName: DEFAULT_DOCTOR_NAME,
    patientId: "",
    patientName: "",
    authId: "",
    diagCombId: "",
    appointmentDate: "",
    typeVisit: "",
    notes: "",
    conformation: "Completed",
  });

  const [authorizations, setAuthorizations] = useState([]);
  const [selectedAuthorization, setSelectedAuthorization] = useState(null);
  const [creatorData, setCreatorData] = useState(null);

  const [selectedDiagData, setSelectedDiagData] = useState({
    diagnosisSet: "",
    procedureSet: "",
  });

  const [existingSuperbillDates, setExistingSuperbillDates] = useState([]);
  const [allowedTypeVisitOptions, setAllowedTypeVisitOptions] = useState([]);

  const [activeHold, setActiveHold] = useState(null);
  const [checkingHold, setCheckingHold] = useState(false);

  const [loadingAuths, setLoadingAuths] = useState(false);
  const [loadingCreatorData, setLoadingCreatorData] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [confirmation, setConfirmation] = useState("");
  const [warning, setWarning] = useState("");
  const [error, setError] = useState("");

  const authService = new PatientAuthorizationService();
  const holdService = new AuthorizationHoldService();
  const superbillService = new SuperbillService();

  useEffect(() => {
    const role = localStorage.getItem("role")?.replace("ROLE_", "") || "";

    if (!["ADMIN", "MANAGER"].includes(role)) {
      alert("You do not have access to create superbills.");
      navigate("/");
    }
  }, [navigate]);

  useEffect(() => {
    if (!confirmation) return;

    const timer = setTimeout(() => {
      setConfirmation("");
    }, 10000);

    return () => clearTimeout(timer);
  }, [confirmation]);

  useEffect(() => {
    const listener = async (e) => {
      const { inputTarget, value, displayName } = e.data || {};

      if (inputTarget === "selectedPatientId") {
        setForm((prev) => ({
          ...prev,
          patientId: value,
          patientName: displayName || "",
          authId: "",
          diagCombId: "",
          appointmentDate: "",
          typeVisit: "",
          officeId: "",
          officeName: "",
          doctorId: DEFAULT_DOCTOR_ID,
          doctorName: DEFAULT_DOCTOR_NAME,
        }));

        setSelectedAuthorization(null);
        setCreatorData(null);
        setExistingSuperbillDates([]);
        setAllowedTypeVisitOptions([]);
        setSelectedDiagData({
          diagnosisSet: "",
          procedureSet: "",
        });

        setActiveHold(null);
        setWarning("");
        setError("");

        await checkPatientAuthorizationHold(value);
        await loadAuthorizations(value);
      }

      if (inputTarget === "officeId" || inputTarget === "superbillOfficeId") {
        setForm((prev) => ({
          ...prev,
          officeId: value,
          officeName: displayName || `Office #${value}`,
        }));

        setWarning("");
      }
    };

    window.addEventListener("message", listener);

    return () => window.removeEventListener("message", listener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getHoldFlags = (hold) => {
    return Array.isArray(hold?.flags) ? hold.flags : [];
  };

  const holdAllowsSuperbillWhileOnHold = (hold) => {
    return getHoldFlags(hold).some(
      (flag) =>
        String(flag?.flagKey || "").toUpperCase() === ALLOW_SUPERBILL_FLAG_KEY
    );
  };

  const isSuperbillBlockedByHold = () => {
    if (!activeHold) return false;

    return !holdAllowsSuperbillWhileOnHold(activeHold);
  };

  const formatHoldStatus = (status) => {
    return String(status || "UNKNOWN").replaceAll("_", " ");
  };

  const getActiveHoldWarningMessage = (hold) => {
    if (!hold) return "";

    if (holdAllowsSuperbillWhileOnHold(hold)) {
      return `This patient has an active authorization hold: ${formatHoldStatus(
        hold.status
      )}, but the hold has "Allow Superbill While On Hold", so superbill creation is allowed.`;
    }

    return `This patient has an active authorization hold: ${formatHoldStatus(
      hold.status
    )}. Superbill creation is blocked until the hold is completed.`;
  };

  const checkPatientAuthorizationHold = async (patientId) => {
    if (!patientId) return;

    try {
      setCheckingHold(true);

      const result = await holdService.getPatientActiveHold(patientId);

      if (result?.activeHold && result?.hold) {
        setActiveHold(result.hold);
        setWarning(getActiveHoldWarningMessage(result.hold));
      } else {
        setActiveHold(null);
      }
    } catch (err) {
      console.error("Could not check authorization hold:", err);
      setActiveHold(null);
    } finally {
      setCheckingHold(false);
    }
  };

  const loadAuthorizations = async (patientId) => {
    if (!patientId) return;

    try {
      setLoadingAuths(true);
      setAuthorizations([]);

      const auths = await authService.findByPatientId(patientId);
      setAuthorizations(Array.isArray(auths) ? auths : []);
    } catch (err) {
      console.error("Failed to load authorizations:", err);
      setAuthorizations([]);
      alert("Could not load authorizations for this patient.");
    } finally {
      setLoadingAuths(false);
    }
  };

  const normalizeActive = (value) => {
    return String(value || "").trim().toLowerCase();
  };

  const isActiveYes = (auth) => {
    return normalizeActive(auth?.active) === "yes";
  };

  const getAuthId = (auth) => {
    return auth?.authId || auth?.authorizationId || auth?.id;
  };

  const getAuthCode = (auth) => {
    return auth?.authCode || auth?.authorizationCode || "No code";
  };

  const getInsptId = (auth) => {
    return auth?.inspt_id || auth?.insptId || auth?.insurancePatientId || "";
  };

  const getStartDate = (auth) => {
    return (
      auth?.dayStart ||
      auth?.startDate ||
      auth?.authorizationStart ||
      auth?.fromDate ||
      creatorData?.startDate
    );
  };

  const getEndDate = (auth) => {
    return (
      auth?.dayEnd ||
      auth?.endDate ||
      auth?.authorizationEnd ||
      auth?.toDate ||
      creatorData?.endDate
    );
  };

  const getVisits = (auth) => {
    const visits =
      creatorData?.remainingVisits !== undefined &&
      creatorData?.remainingVisits !== null
        ? creatorData.remainingVisits
        : auth?.visits;

    return Number.isFinite(Number(visits)) ? Number(visits) : 0;
  };

  const getTotalVisits = (auth) => {
    const totalVisits =
      creatorData?.totalVisits !== undefined && creatorData?.totalVisits !== null
        ? creatorData.totalVisits
        : auth?.totalVisits;

    return Number.isFinite(Number(totalVisits)) ? Number(totalVisits) : 0;
  };

  const isSimpleNoAuthorizationCode = (authCode) => {
    if (!authCode) return false;

    const cleaned = String(authCode).trim();

    if (!/^\d+$/.test(cleaned)) {
      return false;
    }

    const number = parseInt(cleaned, 10);

    return number >= 1 && number <= 99;
  };

  const isEvalVisit = (typeVisit) => {
    return String(typeVisit || "").trim().toLowerCase() === "eval";
  };

  const isYearlyLimitEnforced = () => {
    return creatorData?.yearlyVisitsEnforced === true;
  };

  const getYearlyVisitRemaining = () => {
    const remaining = creatorData?.yearlyVisitRemaining;
    return Number.isFinite(Number(remaining)) ? Number(remaining) : 365;
  };

  const getYearlyVisitTotal = () => {
    const total = creatorData?.yearlyVisitTotal;
    return Number.isFinite(Number(total)) ? Number(total) : 365;
  };

  const insuranceYearlyVisitsBlocked = () => {
    if (!creatorData) return false;
    if (!isYearlyLimitEnforced()) return false;
    if (isEvalVisit(form.typeVisit)) return false;

    return getYearlyVisitRemaining() <= 0;
  };

  const formatDisplayDate = (value) => {
    if (!value) return "N/A";

    const parts = String(value).split("-");

    if (parts.length !== 3) {
      return value;
    }

    const [year, month, day] = parts;
    return `${month}-${day}-${year}`;
  };

  const formatCommaList = (value) => {
    if (!value || !String(value).trim()) {
      return <i>None</i>;
    }

    return String(value)
      .split(",")
      .map((item, index) => (
        <li key={`${item}-${index}`}>{item.trim()}</li>
      ));
  };

  const handleChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));

    if (key === "typeVisit" && creatorData) {
      if (
        creatorData.yearlyVisitsEnforced === true &&
        !isEvalVisit(value) &&
        getYearlyVisitRemaining() <= 0
      ) {
        setWarning(
          "This insurance has 0 yearly visits remaining. Non-eval superbills are blocked until the yearly reset."
        );
      } else if (
        creatorData.yearlyVisitsEnforced === true &&
        !isEvalVisit(value)
      ) {
        setWarning(
          `This non-eval visit will subtract 1 yearly insurance visit. Remaining yearly visits: ${getYearlyVisitRemaining()}.`
        );
      } else if (isEvalVisit(value)) {
        setWarning("Eval visits do not subtract from insurance yearly visits.");
      } else if (activeHold) {
        setWarning(getActiveHoldWarningMessage(activeHold));
      } else {
        setWarning("");
      }
    }
  };

  const selectedDateAlreadyExists = () => {
    if (!form.appointmentDate) return false;

    return existingSuperbillDates.some(
      (date) => String(date) === String(form.appointmentDate)
    );
  };

  const handleAuthorizationChange = async (authId) => {
    setWarning("");
    setError("");
    setCreatorData(null);
    setExistingSuperbillDates([]);
    setAllowedTypeVisitOptions([]);
    setSelectedDiagData({
      diagnosisSet: "",
      procedureSet: "",
    });

    if (activeHold) {
      setWarning(getActiveHoldWarningMessage(activeHold));
    }

    if (!authId) {
      setForm((prev) => ({
        ...prev,
        authId: "",
        diagCombId: "",
        appointmentDate: "",
        typeVisit: "",
        officeId: "",
        officeName: "",
        doctorId: DEFAULT_DOCTOR_ID,
        doctorName: DEFAULT_DOCTOR_NAME,
      }));

      setSelectedAuthorization(null);
      return;
    }

    const auth = authorizations.find(
      (item) => String(getAuthId(item)) === String(authId)
    );

    setSelectedAuthorization(auth || null);

    try {
      setLoadingCreatorData(true);

      const data = await superbillService.getCreatorData(authId);

      setCreatorData(data || null);

      setExistingSuperbillDates(
        Array.isArray(data?.existingSuperbillDates)
          ? data.existingSuperbillDates
          : []
      );

      setAllowedTypeVisitOptions(
        Array.isArray(data?.allowedTypeVisitOptions)
          ? data.allowedTypeVisitOptions
          : []
      );

      setSelectedDiagData({
        diagnosisSet: data?.diagnosisSet || "",
        procedureSet: data?.procedureSet || "",
      });

      setForm((prev) => ({
        ...prev,
        authId,
        diagCombId: data?.diagCombId || "",
        appointmentDate: "",
        typeVisit: "",
        officeId: data?.officeId || "",
        officeName: data?.officeName || "",
        doctorId: data?.doctorId || DEFAULT_DOCTOR_ID,
        doctorName: data?.doctorName || DEFAULT_DOCTOR_NAME,
      }));

      if (!data?.officeId) {
        setWarning(
          "This patient insurance does not have a default office. Please choose an office before creating the superbill."
        );
      } else if (auth && !isActiveYes(auth)) {
        setWarning(
          "This authorization is not active. It cannot be used to create a superbill."
        );
      } else if (data?.remainingVisits === 0) {
        setWarning("This authorization has 0 visits remaining.");
      } else if (
        data?.yearlyVisitsEnforced === true &&
        Number(data?.yearlyVisitRemaining || 0) <= 0
      ) {
        setWarning(
          "This insurance has 0 yearly visits remaining. Only eval visits are allowed until yearly reset."
        );
      } else if (data?.remainingVisits < 4) {
        setWarning(
          `Heads up: this authorization only has ${data.remainingVisits} visit(s) remaining.`
        );
      } else if (activeHold) {
        setWarning(getActiveHoldWarningMessage(activeHold));
      }
    } catch (err) {
      console.error("Failed to load creator data:", err);

      setForm((prev) => ({
        ...prev,
        authId,
        diagCombId: "",
        appointmentDate: "",
        typeVisit: "",
        officeId: "",
        officeName: "",
        doctorId: DEFAULT_DOCTOR_ID,
        doctorName: DEFAULT_DOCTOR_NAME,
      }));

      setWarning(
        "Could not load all authorization details. Please try selecting the authorization again."
      );
    } finally {
      setLoadingCreatorData(false);
    }
  };

  const isDateInsideAuthorization = (date, auth) => {
    if (!date || !auth) return false;

    const start = getStartDate(auth);
    const end = getEndDate(auth);

    if (start && date < start) return false;
    if (end && date > end) return false;

    return true;
  };

  const handleDateChange = (date) => {
    setWarning("");
    setError("");

    setForm((prev) => ({
      ...prev,
      appointmentDate: date,
    }));

    if (isSuperbillBlockedByHold()) {
      setWarning(getActiveHoldWarningMessage(activeHold));
      return;
    }

    if (activeHold && holdAllowsSuperbillWhileOnHold(activeHold)) {
      setWarning(getActiveHoldWarningMessage(activeHold));
    }

    if (!selectedAuthorization || !date) {
      return;
    }

    if (!isDateInsideAuthorization(date, selectedAuthorization)) {
      setWarning(
        `Selected date is outside this authorization range: ${
          formatDisplayDate(getStartDate(selectedAuthorization)) || "No start"
        } to ${
          formatDisplayDate(getEndDate(selectedAuthorization)) || "No end"
        }.`
      );
      return;
    }

    if (existingSuperbillDates.includes(date)) {
      setWarning(
        `A superbill already exists for this authorization on ${formatDisplayDate(
          date
        )}. Choose a different date.`
      );
    }
  };

  const validateTypeVisit = () => {
    if (!form.typeVisit) {
      alert("Please select a type of visit.");
      return false;
    }

    if (
      allowedTypeVisitOptions.length > 0 &&
      !allowedTypeVisitOptions.includes(form.typeVisit)
    ) {
      alert(
        `This type of visit is not allowed right now. Allowed options: ${allowedTypeVisitOptions.join(
          ", "
        )}`
      );
      return false;
    }

    if (insuranceYearlyVisitsBlocked()) {
      alert(
        "This insurance has 0 yearly visits remaining. Non-eval superbills are blocked until the yearly reset."
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    setError("");
    setConfirmation("");

    if (checkingHold || loadingCreatorData) {
      alert("Still loading authorization information. Please wait.");
      return;
    }

    if (isSuperbillBlockedByHold()) {
      alert(
        `This patient has an active authorization hold: ${formatHoldStatus(
          activeHold.status
        )}. Complete or close the hold before creating a superbill.`
      );
      return;
    }

    if (!form.patientId) {
      alert("Please select a patient.");
      return;
    }

    if (!form.authId || !selectedAuthorization) {
      alert("Please select an authorization.");
      return;
    }

    if (!isActiveYes(selectedAuthorization)) {
      alert("This authorization is not active.");
      return;
    }

    if (getVisits(selectedAuthorization) <= 0) {
      alert("This authorization has no visits remaining.");
      return;
    }

    if (!form.diagCombId) {
      alert("Diagnosis/procedure record is missing for this authorization.");
      return;
    }

    if (!form.officeId) {
      alert("Please choose an office before creating this superbill.");
      return;
    }

    if (!form.doctorId) {
      setForm((prev) => ({
        ...prev,
        doctorId: DEFAULT_DOCTOR_ID,
        doctorName: DEFAULT_DOCTOR_NAME,
      }));
    }

    if (!form.appointmentDate) {
      alert("Please select a date.");
      return;
    }

    if (!isDateInsideAuthorization(form.appointmentDate, selectedAuthorization)) {
      alert("Selected date is outside the authorization date range.");
      return;
    }

    if (selectedDateAlreadyExists()) {
      alert(
        `A superbill already exists for this authorization on ${formatDisplayDate(
          form.appointmentDate
        )}. Please choose a different date.`
      );
      return;
    }

    try {
      const existsResponse = await superbillService.dateExistsForAuth(
        form.authId,
        form.appointmentDate
      );

      if (existsResponse?.exists) {
        alert(
          `A superbill already exists for this authorization on ${formatDisplayDate(
            form.appointmentDate
          )}. Please choose a different date.`
        );

        const refreshedData = await superbillService.getCreatorData(form.authId);

        setExistingSuperbillDates(
          Array.isArray(refreshedData?.existingSuperbillDates)
            ? refreshedData.existingSuperbillDates
            : []
        );

        return;
      }
    } catch (err) {
      console.error("Could not verify duplicate superbill date:", err);

      const proceed = window.confirm(
        "Could not verify if this date already exists. Continue anyway?"
      );

      if (!proceed) return;
    }

    if (!validateTypeVisit()) {
      return;
    }

    if (getVisits(selectedAuthorization) < 4) {
      const confirmed = window.confirm(
        `Heads up: this authorization only has ${getVisits(
          selectedAuthorization
        )} visit(s) remaining. Continue?`
      );

      if (!confirmed) return;
    }

    if (
      creatorData?.yearlyVisitsEnforced === true &&
      !isEvalVisit(form.typeVisit)
    ) {
      const confirmed = window.confirm(
        `This will subtract 1 insurance yearly visit. Remaining before submit: ${getYearlyVisitRemaining()}. Continue?`
      );

      if (!confirmed) return;
    }

    const finalData = {
      officeId: parseInt(form.officeId, 10),
      doctorId: parseInt(form.doctorId || DEFAULT_DOCTOR_ID, 10),
      diagCombId: parseInt(form.diagCombId, 10),
      appointmentDate: form.appointmentDate,
      appointmentTime: null,
      notes: form.notes,
      typeVisit: form.typeVisit,
      conformation: "Completed",
    };

    try {
      setSubmitting(true);

      const result = await appointmentAPI.createSuperbillAppointment(finalData);

      if (result?.success) {
        setConfirmation(
          `Superbill created successfully. Appointment ID: ${
            result.appointmentId || "created"
          }. Remaining authorization visits: ${
            result.remainingVisits ?? "unknown"
          }.`
        );

        if (result.warning) {
          setWarning(result.warning);
        }

        if (form.patientId) {
          await checkPatientAuthorizationHold(form.patientId);
          await loadAuthorizations(form.patientId);
        }

        setSelectedAuthorization(null);
        setCreatorData(null);
        setSelectedDiagData({
          diagnosisSet: "",
          procedureSet: "",
        });

        setExistingSuperbillDates([]);
        setAllowedTypeVisitOptions([]);

        setForm((prev) => ({
          ...prev,
          authId: "",
          diagCombId: "",
          appointmentDate: "",
          typeVisit: "",
          notes: "",
          officeId: "",
          officeName: "",
          doctorId: DEFAULT_DOCTOR_ID,
          doctorName: DEFAULT_DOCTOR_NAME,
          conformation: "Completed",
        }));
      } else {
        setError(result?.message || "Failed to create superbill.");
      }
    } catch (err) {
      console.error("Error creating superbill appointment:", err);
      setError(err.message || "An error occurred while creating the superbill.");
    } finally {
      setSubmitting(false);
    }
  };

  const getAuthorizationLabel = (auth) => {
    const authId = getAuthId(auth);
    const authCode = getAuthCode(auth);
    const startDate = getStartDate(auth);
    const endDate = getEndDate(auth);
    const visits = auth?.visits ?? 0;
    const totalVisits = auth?.totalVisits ?? 0;
    const active = isActiveYes(auth) ? "Yes" : "No";
    const insptId = getInsptId(auth);

    const noFormalAuth = isSimpleNoAuthorizationCode(authCode);

    if (noFormalAuth) {
      return `No Auth Needed | Auth #${authId} | InsPt #${
        insptId || "N/A"
      } | Active: ${active} | Remaining: ${visits}`;
    }

    return `Code: ${authCode} | Auth #${authId} | InsPt #${
      insptId || "N/A"
    } | Active: ${active} | Total: ${totalVisits} | Remaining: ${visits} | ${
      startDate || "No start"
    } - ${endDate || "No end"}`;
  };

  const openPatientSearch = () => {
    const popupUrl = `/search-popup?type=Patient&inputTarget=selectedPatientId&url=${encodeURIComponent(
      apiBaseUrl + "/patientinfo/search/all"
    )}`;

    window.open(popupUrl, "_blank", "width=700,height=700");
  };

  const openOfficeSearch = () => {
    window.open("/office-search", "popup", "width=700,height=700");
  };

  const duplicateDateSelected = selectedDateAlreadyExists();
  const superbillBlockedByHold = isSuperbillBlockedByHold();
  const holdHasSuperbillOverride = holdAllowsSuperbillWhileOnHold(activeHold);

  return (
    <div className="superbill-page">
      <div className="superbill-header">
        <div>
          <h2>Create Superbill</h2>
          <p>
            Create an old completed appointment record and automatically create
            the superbill.
          </p>
        </div>
      </div>

      {confirmation && (
        <div className="modal">
          <div className="modal-content" style={{ position: "relative" }}>
            <button
              type="button"
              onClick={() => setConfirmation("")}
              className="superbill-modal-close"
              aria-label="Close confirmation"
            >
              ×
            </button>

            <h3 style={{ paddingRight: "35px" }}>{confirmation}</h3>
          </div>
        </div>
      )}

      {activeHold && (
        <div
          className={
            holdHasSuperbillOverride
              ? "superbill-alert superbill-alert-warning"
              : "superbill-alert superbill-alert-danger"
          }
        >
          <strong>Authorization Hold Active:</strong>{" "}
          {formatHoldStatus(activeHold.status)}
          <br />
          <strong>Reason:</strong> {activeHold.holdReason || "N/A"}
          <br />
          <strong>Hold ID:</strong> {activeHold.holdId}

          {holdHasSuperbillOverride && (
            <>
              <br />
              <strong>Override:</strong> Superbill creation is allowed because
              this hold has the "Allow Superbill While On Hold" flag.
            </>
          )}
        </div>
      )}

      {warning && (
        <div className="superbill-alert superbill-alert-warning">
          {warning}
        </div>
      )}

      {error && (
        <div className="superbill-alert superbill-alert-danger">{error}</div>
      )}

      <div className="superbill-grid">
        <section className="superbill-card">
          <h3>Patient</h3>

          <label>Patient</label>
          <div className="input-container">
            <button type="button" onClick={openPatientSearch}>
              Search Patient
            </button>

            {checkingHold && <p>Checking authorization hold...</p>}

            {form.patientName && (
              <div className="selected-box">
                <span>Selected Patient</span>
                <strong>{form.patientName}</strong>
              </div>
            )}
          </div>

          <label>Authorization</label>
          <div className="input-container">
            {loadingAuths ? (
              <p>Loading authorizations...</p>
            ) : (
              <select
                value={form.authId}
                onChange={(e) => handleAuthorizationChange(e.target.value)}
                disabled={!form.patientId || authorizations.length === 0}
              >
                <option value="">
                  {form.patientId
                    ? authorizations.length > 0
                      ? "Select authorization"
                      : "No authorizations found"
                    : "Select patient first"}
                </option>

                {authorizations.map((auth) => {
                  const authId = getAuthId(auth);

                  return (
                    <option key={authId} value={authId}>
                      {getAuthorizationLabel(auth)}
                    </option>
                  );
                })}
              </select>
            )}

            {loadingCreatorData && <p>Loading authorization details...</p>}
          </div>
        </section>

        <section className="superbill-card">
          <h3>Authorization & Insurance Details</h3>

          {selectedAuthorization && creatorData ? (
            <>
              <div className="summary-list">
                {creatorData.noFormalAuthorizationRequired ? (
                  <p>
                    <strong>Authorization:</strong> No formal authorization
                    required
                  </p>
                ) : (
                  <>
                    <p>
                      <strong>Authorization Code:</strong>{" "}
                      {creatorData.authCode || "N/A"}
                    </p>

                    <p>
                      <strong>Authorization Total Visits:</strong>{" "}
                      {creatorData.totalVisits ?? "N/A"}
                    </p>
                  </>
                )}

                <p>
                  <strong>Active:</strong> {creatorData.active || "N/A"}
                </p>

                <p>
                  <strong>Authorization Remaining Visits:</strong>{" "}
                  {creatorData.remainingVisits ?? "N/A"}
                </p>

                {!creatorData.noFormalAuthorizationRequired && (
                  <p>
                    <strong>Date Range:</strong>{" "}
                    {formatDisplayDate(creatorData.startDate)} to{" "}
                    {formatDisplayDate(creatorData.endDate)}
                  </p>
                )}

                <p>
                  <strong>Insurance Name:</strong>{" "}
                  {creatorData.insuranceName || "N/A"}
                </p>

                <p>
                  <strong>Insurance Patient ID:</strong>{" "}
                  {creatorData.insptId || "N/A"}
                </p>

                <p>
                  <strong>Yearly Insurance Visits:</strong>{" "}
                  {creatorData.yearlyVisitsEnforced
                    ? `${creatorData.yearlyVisitRemaining ?? "N/A"} remaining / ${
                        creatorData.yearlyVisitTotal ?? "N/A"
                      } total`
                    : "Unlimited / Not Counted"}
                </p>

                {creatorData.yearlyVisitsEnforced && (
                  <p>
                    <strong>Yearly Reset Year:</strong>{" "}
                    {creatorData.yearlyVisitResetYear || "N/A"}
                  </p>
                )}
              </div>

              <div className="superbill-sub-card">
                <strong>Existing Superbill Dates</strong>

                {existingSuperbillDates.length > 0 ? (
                  <div className="date-chip-wrap">
                    {existingSuperbillDates.map((date) => (
                      <span
                        key={date}
                        className={
                          form.appointmentDate === date
                            ? "date-chip date-chip-danger"
                            : "date-chip"
                        }
                      >
                        {formatDisplayDate(date)}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p>No superbill dates have been used for this authorization yet.</p>
                )}
              </div>
            </>
          ) : (
            <p className="empty-state">
              Select a patient and authorization to view details.
            </p>
          )}
        </section>

        <section className="superbill-card">
          <h3>Office, Doctor & Diagnosis</h3>

          {selectedAuthorization && creatorData ? (
            <>
              <div className="summary-list">
                <p>
                  <strong>Office:</strong>{" "}
                  {form.officeId
                    ? form.officeName || `Office #${form.officeId}`
                    : "Missing"}
                </p>

                <button type="button" onClick={openOfficeSearch}>
                  {form.officeId ? "Change Office" : "Search Office"}
                </button>

                <p>
                  <strong>Doctor:</strong>{" "}
                  {form.doctorName || DEFAULT_DOCTOR_NAME}
                </p>

                <p>
                  <strong>Doctor ID:</strong>{" "}
                  {form.doctorId || DEFAULT_DOCTOR_ID}
                </p>

                <p>
                  <strong>Subscriber:</strong> {creatorData.subscriber || "N/A"}
                </p>

                <p>
                  <strong>Insurance Active / Type Visits:</strong>{" "}
                  {creatorData.insuranceActive || "N/A"}
                </p>
              </div>

              {form.diagCombId && (
                <div className="superbill-sub-card">
                  <p>
                    <strong>Diagnosis/Procedure Record ID:</strong>{" "}
                    {form.diagCombId}
                  </p>

                  <div>
                    <strong>Diagnosis:</strong>
                    <ul>{formatCommaList(selectedDiagData.diagnosisSet)}</ul>
                  </div>

                  <div>
                    <strong>Procedure:</strong>
                    <ul>{formatCommaList(selectedDiagData.procedureSet)}</ul>
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="empty-state">
              Office, doctor, diagnosis, and procedure will appear after an
              authorization is selected.
            </p>
          )}
        </section>
      </div>

      <section className="superbill-card superbill-bottom-card">
        <h3>Superbill Information</h3>

        <div className="superbill-bottom-grid">
          <div>
            <label>Date</label>
            <input
              type="date"
              value={form.appointmentDate}
              min={
                selectedAuthorization
                  ? getStartDate(selectedAuthorization) || undefined
                  : undefined
              }
              max={
                selectedAuthorization
                  ? getEndDate(selectedAuthorization) || undefined
                  : undefined
              }
              onChange={(e) => handleDateChange(e.target.value)}
              disabled={!selectedAuthorization || superbillBlockedByHold}
              className={duplicateDateSelected ? "input-danger" : ""}
            />

            {selectedAuthorization && (
              <p className="info-text">
                Date must be between{" "}
                {formatDisplayDate(getStartDate(selectedAuthorization))} and{" "}
                {formatDisplayDate(getEndDate(selectedAuthorization))}.
              </p>
            )}
          </div>

          <div>
            <label>Type of Visit</label>
            <select
              value={form.typeVisit}
              onChange={(e) => handleChange("typeVisit", e.target.value)}
              disabled={
                superbillBlockedByHold || !selectedAuthorization || loadingCreatorData
              }
            >
              <option value="">Select type of visit</option>

              {allowedTypeVisitOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>

            {selectedAuthorization && allowedTypeVisitOptions.length > 0 && (
              <p className="info-text">
                Available options: {allowedTypeVisitOptions.join(", ")}
              </p>
            )}

            {creatorData?.yearlyVisitsEnforced && (
              <p className="info-text">
                Eval does not count against yearly insurance visits. Any other
                type subtracts 1.
              </p>
            )}
          </div>

          <div className="notes-column">
            <label>Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
              placeholder="Superbill notes"
              disabled={superbillBlockedByHold}
            />
          </div>
        </div>

        {duplicateDateSelected && (
          <div className="superbill-alert superbill-alert-danger">
            A superbill already exists on{" "}
            {formatDisplayDate(form.appointmentDate)} for this authorization.
            Choose a different date.
          </div>
        )}

        {insuranceYearlyVisitsBlocked() && (
          <div className="superbill-alert superbill-alert-danger">
            This insurance has 0 yearly visits remaining. Non-eval superbills
            are blocked until the yearly reset. Choose eval only if appropriate.
          </div>
        )}

        <div className="superbill-actions">
          <button
            className="submit-button"
            type="button"
            disabled={
              submitting ||
              superbillBlockedByHold ||
              duplicateDateSelected ||
              loadingCreatorData ||
              insuranceYearlyVisitsBlocked()
            }
            onClick={handleSubmit}
          >
            {superbillBlockedByHold
              ? "Patient On Authorization Hold"
              : duplicateDateSelected
              ? "Duplicate Date Not Allowed"
              : insuranceYearlyVisitsBlocked()
              ? "Insurance Yearly Visits Exhausted"
              : loadingCreatorData
              ? "Loading Authorization..."
              : submitting
              ? "Creating..."
              : "Create Superbill"}
          </button>
        </div>
      </section>
    </div>
  );
};

export default SuperbillCreator;